import { Uuid } from "@/domains/global/identifiers";
import { VerificationStatus } from "@/domains/integrations/verification-status";
import { EmailSenderRaw } from "@/store/email-senders/types";
import { action, computed, makeObservable } from "mobx";
import { observable } from "mobx";

export class EmailSenderObservable {
  private _id: Uuid;
  private _spaceAccountId: Uuid;
  private _verified: boolean;
  private _email: string;
  private _verificationToken: string | undefined;
  private _verificationStatus: VerificationStatus;
  private _canDelete: boolean;

  constructor({ id, email, is_verified, space_account_id, can_delete = true }: EmailSenderRaw) {
    this._id = id;
    this._email = email;
    this._verified = is_verified;
    this._spaceAccountId = space_account_id;
    this._canDelete = can_delete;
    this._verificationStatus = is_verified
      ? VerificationStatus.Verified
      : VerificationStatus.Unverified;

    makeObservable<
      EmailSenderObservable,
      | "_id"
      | "_email"
      | "_verified"
      | "_spaceAccountId"
      | "_verificationToken"
      | "_verificationStatus"
      | "_canDelete"
    >(this, {
      _id: observable,
      id: computed,
      setId: action,
      reset: action,

      _spaceAccountId: observable,
      spaceAccountId: computed,
      setSpaceAccountId: action,

      _verified: observable,
      isVerified: computed,

      _email: observable,
      input: computed,
      setInput: action,
      isInputValid: computed,

      _verificationStatus: observable,
      verificationStatus: computed,
      setVerificationStatus: action,

      _verificationToken: observable,
      setVerificationToken: action,
      verificationToken: computed,

      _canDelete: observable,
      canDelete: computed,
      setCanDelete: action,

      emailDomain: computed,
    });
  }

  get id() {
    return this._id;
  }

  get spaceAccountId() {
    return this._spaceAccountId;
  }

  get isVerified() {
    return this._verified;
  }

  get input() {
    return this._email;
  }

  get isInputValid() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(this._email);
  }

  get verificationToken() {
    return this._verificationToken;
  }

  get verificationStatus() {
    return this._verificationStatus;
  }

  get canDelete() {
    return this._canDelete;
  }

  get emailDomain() {
    const atIndex = this._email.indexOf("@");
    if (atIndex === -1) return null;
    return this._email.slice(atIndex + 1);
  }

  setId = (id: Uuid) => {
    this._id = id;
  };

  setSpaceAccountId = (spaceAccountId: Uuid) => {
    this._spaceAccountId = spaceAccountId;
  };

  setInput = (input: string) => {
    this._email = input;
  };

  setVerificationToken = (verificationToken: string) => {
    this._verificationToken = verificationToken;

    if (this.verificationStatus === VerificationStatus.Invalid) {
      this.setVerificationStatus(VerificationStatus.AwaitsVerification);
    }
  };

  setVerificationStatus = (verificationStatus: VerificationStatus) => {
    this._verificationStatus = verificationStatus;

    this._verified = verificationStatus === VerificationStatus.Verified;
  };

  setCanDelete = (canDelete: boolean) => {
    this._canDelete = canDelete;
  };

  reset = (data: EmailSenderRaw) => {
    this._id = data.id;
    this._email = data.email;
    this._verified = data.is_verified;
    this._spaceAccountId = data.space_account_id;
  };
}
